import React from 'react';
import { TrendingUp, TrendingDown, ChevronUp, ChevronDown, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '../ui/button';

// Panel move buttons - standalone component
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast, lightTheme = false }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);

const PerformanceLeadersPanel = ({
  stocks,
  showMoversChart,
  toggleMoversChart,
  moversPeriod,
  setMoversPeriod,
  mobilePreview,
  marketStatus,
  formatCurrencyPrivate,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
}) => {
  // Define market asset types - only these should appear in Performance Leaders
  const MARKET_TYPES = ['STOCK', 'BOND', 'BULLION', 'CRYPTO'];
  
  // Filter to only market assets (exclude CASH, VEHICLES, REAL ESTATE, JEWELRY, etc.)
  const marketStocks = stocks.filter(stock => {
    const stockType = (stock.investmentType || '').toUpperCase();
    return MARKET_TYPES.includes(stockType);
  });
  
  // Calculate period-based gains for each stock
  // NOTE: We only have reliable data for 1D (dailyChangePercent from API) and ALL-TIME (from cost basis)
  // For 1W, 1M, 1Y we use all-time data since we don't have per-stock historical prices
  const stocksWithGains = marketStocks.map(stock => {
    const totalValue = stock.currentPrice * stock.shares;
    const costTotal = stock.costBasis * stock.shares;
    const allTimeChange = totalValue - costTotal;
    const allTimeChangePercent = costTotal > 0 ? ((totalValue - costTotal) / costTotal) * 100 : 0;
    
    let gainLossPercent, gainLoss;
    
    if (moversPeriod === '1D') {
      // Use REAL daily change data from API
      gainLossPercent = stock.dailyChangePercent || 0;
      gainLoss = (stock.dailyChange || 0) * stock.shares;
    } else {
      // For other periods (1W, 1M, 1Y), use the all-time gain/loss
      // This is the most accurate data we have per-stock
      gainLossPercent = allTimeChangePercent;
      gainLoss = allTimeChange;
    }
    
    return { ...stock, gainLoss, gainLossPercent, totalValue };
  });
  
  // Separate gainers and losers based on period performance
  const gainers = [...stocksWithGains]
    .filter(s => s.gainLossPercent > 0)
    .sort((a, b) => b.gainLossPercent - a.gainLossPercent)
    .slice(0, 5);
  
  const losers = [...stocksWithGains]
    .filter(s => s.gainLossPercent < 0)
    .sort((a, b) => a.gainLossPercent - b.gainLossPercent)
    .slice(0, 5);
  
  const maxGainerPercent = gainers.length > 0 ? Math.max(...gainers.map(s => s.gainLossPercent)) : 1;
  const maxLoserPercent = losers.length > 0 ? Math.max(...losers.map(s => Math.abs(s.gainLossPercent))) : 1;
  
  // Show appropriate label based on period
  // For 1W, 1M, 1Y we show "All Time" since we're using all-time data per stock
  const periodLabel = moversPeriod === '1D' 
    ? (marketStatus.status === 'open' ? 'today' : 'last close') 
    : 'all time';

  return (
    <div className={`${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'} scroll-mt-32 sm:scroll-mt-0`}>
      <div className="bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden">
        {/* Mobile Header - Clickable to expand/collapse */}
        <div 
          className="sm:hidden bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-3 cursor-pointer"
          onClick={toggleMoversChart}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Performance Leaders</h2>
                <p className="text-[10px] text-white/70">Top gainers & losers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Period indicator badge - Mobile */}
              <div className="flex items-center gap-1 px-2 py-1 bg-white/20 rounded-full">
                <span className="text-[10px] font-semibold text-white">{moversPeriod === '1D' ? (marketStatus.status === 'open' ? 'TODAY' : 'LAST CLOSE') : moversPeriod}</span>
              </div>
              {/* Collapse indicator - Mobile */}
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
                {showMoversChart ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </div>
          </div>
          {/* Time Period Toggle - Mobile (only show when expanded) */}
          {showMoversChart && (
            <div 
              className="flex items-center justify-center bg-white/20 rounded-lg p-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              {['1D', '1W', '1M', '1Y'].map((period) => (
                <button
                  key={period}
                  onClick={() => setMoversPeriod(period)}
                  className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    moversPeriod === period 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-white/80'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Desktop Header */}
        <div 
          className="hidden sm:block bg-gradient-to-r from-indigo-600 to-purple-600 px-4 sm:px-5 py-3 cursor-pointer"
          onClick={toggleMoversChart}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PanelMoveButtons panelId="leaders" movePanelUp={movePanelUp} movePanelDown={movePanelDown} isFirst={isFirst} isLast={isLast} lightTheme={true} />
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Performance Leaders</h2>
                <p className="text-xs text-white/70">Top gainers & losers in your portfolio</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); toggleMoversChart(); }}
              className="text-white/80 hover:text-white hover:bg-white/10 p-1 h-auto"
            >
              {showMoversChart ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Chart Content */}
        {showMoversChart && stocks.length > 0 && (
          <div className="p-4 sm:p-6 bg-gradient-to-b from-slate-50 to-white">
            {/* Time Period Toggle - Inside content */}
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center bg-indigo-100 rounded-lg p-1">
                {['1D', '1W', '1M', '1Y'].map((period) => (
                  <button
                    key={period}
                    onClick={() => setMoversPeriod(period)}
                    className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${
                      moversPeriod === period 
                        ? 'bg-indigo-600 text-white shadow-sm' 
                        : 'text-indigo-600 hover:bg-indigo-200'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Gainers Section */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-emerald-800">Top Gainers</h3>
                    <p className="text-xs text-emerald-600">{gainers.length} winning {periodLabel.toLowerCase()}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {gainers.map((stock, index) => {
                    const barHeight = (stock.gainLossPercent / maxGainerPercent) * 100;
                    return (
                      <div key={stock.id} className="bg-white rounded-lg p-3 shadow-sm border border-emerald-100 hover:shadow-md transition-all hover:border-emerald-300 overflow-hidden">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              index === 0 ? 'bg-amber-400 text-white' :
                              index === 1 ? 'bg-slate-400 text-white' :
                              index === 2 ? 'bg-amber-600 text-white' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {index + 1}
                            </span>
                            <span className="font-bold text-slate-800 text-sm">{stock.symbol}</span>
                          </div>
                          <span className="text-sm font-bold text-emerald-600 flex-shrink-0 whitespace-nowrap">
                            +{stock.gainLossPercent.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2 bg-emerald-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all duration-500"
                            style={{ width: `${barHeight}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-2 text-xs">
                          <span className="text-slate-500">${formatCurrencyPrivate(stock.totalValue)}</span>
                          <span className="text-emerald-600 font-semibold">+${formatCurrencyPrivate(Math.abs(stock.gainLoss))}</span>
                        </div>
                      </div>
                    );
                  })}
                  {gainers.length === 0 && (
                    <div className="text-center py-6 text-emerald-600">
                      <p className="text-sm">No gaining positions {periodLabel.toLowerCase()}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Top Losers Section */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                    <TrendingDown className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-red-800">Top Losers</h3>
                    <p className="text-xs text-red-600">{losers.length} losing {periodLabel.toLowerCase()}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {losers.map((stock, index) => {
                    const barHeight = (Math.abs(stock.gainLossPercent) / maxLoserPercent) * 100;
                    return (
                      <div key={stock.id} className="bg-white rounded-lg p-3 shadow-sm border border-red-100 hover:shadow-md transition-all hover:border-red-300 overflow-hidden">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              index === 0 ? 'bg-red-500 text-white' :
                              index === 1 ? 'bg-red-400 text-white' :
                              index === 2 ? 'bg-red-300 text-white' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {index + 1}
                            </span>
                            <span className="font-bold text-slate-800 text-sm">{stock.symbol}</span>
                          </div>
                          <span className="text-sm font-bold text-red-600 flex-shrink-0 w-[70px] text-right">
                            {stock.gainLossPercent.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2 bg-red-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-red-400 to-orange-500 rounded-full transition-all duration-500"
                            style={{ width: `${barHeight}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-2 text-xs">
                          <span className="text-slate-500">${formatCurrencyPrivate(stock.totalValue)}</span>
                          <span className="text-red-600 font-semibold">-${formatCurrencyPrivate(Math.abs(stock.gainLoss))}</span>
                        </div>
                      </div>
                    );
                  })}
                  {losers.length === 0 && (
                    <div className="text-center py-6 text-red-600">
                      <p className="text-sm">No losing positions {periodLabel.toLowerCase()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Summary Footer */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gradient-to-r from-emerald-400 to-teal-500"></div>
                  <span className="text-slate-600">Profitable positions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-gradient-to-r from-red-400 to-orange-500"></div>
                  <span className="text-slate-600">Underperforming positions</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceLeadersPanel;
